import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowUpRight, ExternalLink, Calendar, MapPin, Award } from "lucide-react";
import Nav from "./Nav";
import Footer from "./Footer";

// ─── Dispatches archive ──────────────────────────────────────────────
// Add new field dispatches at the TOP of this array. Each becomes a
// linkable section + index card.

const DISPATCHES = [
    {
        id: "holi-2026",
        number: "07",
        date: "Spring 2026",
        title: "Colours, all over the office.",
        kicker: "Holi celebration · Team and close friends",
        venue: "Loni, Maharashtra",
        meta: [
            { icon: Calendar, label: "Spring 2026" },
            { icon: MapPin, label: "Around the office · Loni" },
            { icon: Award, label: "Team, students and close friends" },
        ],
        story: [
            "When Holi arrived, we stepped outside and did what every small office in India does that week - colour first, work later. Pink, purple, orange on every face. Music in the background. Strangers on the street joining in.",
            "The kids loved the vibe. The staff loved it more. Bobby walked out of the office in a fresh shirt and walked back in covered in three shades of magenta. The office carpets are still slightly tinted as proof.",
            "These small unscripted afternoons are what holds a small-town team together more than any policy ever could.",
        ],
        photos: [
            { src: "/community/holi-01.jpg", caption: "Team and close friends · colours up · Loni" },
            { src: "/community/holi-02.jpg", caption: "Hands stained · the unmistakable signature of Holi" },
            { src: "/community/holi-03.jpg", caption: "Back at the office · founder and team after the celebration" },
        ],
    },
    {
        id: "team-bonding-cricket",
        number: "05",
        date: "Earlier this year",
        title: "On the turf, off the clock.",
        kicker: "Team bonding · Students, staff & close friends",
        venue: "Loni, Maharashtra",
        meta: [
            { icon: Calendar, label: "Earlier this year" },
            { icon: MapPin, label: "Local turf grounds · Loni" },
            { icon: Award, label: "Students, staff and close friends · invitation only" },
        ],
        story: [
            "Every now and then - always on a weekend so the workweek stays untouched - we put the laptops down, leave the office above Ni3 Computer and head out together. A turf hire, a few bats, a tennis ball, and the rule that everyone bats - no exceptions.",
            "These outings happen on Saturdays because building something in a small town, with a small team, means the line between colleague and friend dissolves quickly. Students who joined as cohort members end up on the same team as the staff who taught them. Close friends from outside the office come along too. The competition is fierce. The food afterwards is fiercer.",
            "We don't post these as marketing. We post them because the kids loved the vibe, and because future cohorts deserve to see what working at IndoBlitz actually looks like once the cameras go down.",
        ],
        photos: [
            { src: "/community/team-cricket-01.jpg", caption: "Pre-match selfie · students and staff at the turf grounds" },
            { src: "/community/team-cricket-02.jpg", caption: "Team and close friends · post-match group photo" },
            { src: "/community/team-cricket-04.jpg", caption: "After the game · the whole crew on the field" },
        ],
    },
    {
        id: "team-lunch-future-plans",
        number: "06",
        date: "Spring 2026 · Weekend",
        title: "Where we are. Where we're going.",
        kicker: "Team lunch · Founder's address",
        venue: "Loni, Maharashtra",
        meta: [
            { icon: Calendar, label: "Spring 2026 · weekend gathering" },
            { icon: MapPin, label: "Restaurant outside Loni · team + close friends" },
            { icon: Award, label: "Founder shares the next chapter for IndoBlitz" },
        ],
        story: [
            "After months of heads-down delivery, we stepped out together - team and close friends - for a long Saturday lunch. The agenda was simple: eat well, then listen.",
            "Standing at the head of the table, Bobby walked the room through what's next for IndoBlitz - the cohorts in the pipeline, the academic partnerships building, the global delivery work coming in, and the role each person in that room would play in it. It wasn't a town hall presentation. It was a meal, with the founder explaining the next chapter to the people building it with him.",
            "These gatherings happen a few times a year. They're how a small team in a small town stays aligned without ever needing a deck.",
        ],
        photos: [
            { src: "/community/team-lunch-02.jpg", caption: "Founder addresses the team and close friends over Saturday lunch" },
            { src: "/community/team-lunch-04.jpg", caption: "Around the long table · team and close friends" },
            { src: "/community/team-lunch-03.jpg", caption: "The IndoBlitz crew on the balcony · Pragya Shakti Foundation banner displayed alongside" },
            { src: "/community/team-lunch-01.jpg", caption: "Back at the office · the workspace where every plan becomes work" },
        ],
    },
    {
        id: "swati-industry-talk",
        number: "04",
        date: "02 April 2026",
        title: "From rural college to global tech - an alumna comes home.",
        kicker: "Industry talk · Alumni mentorship",
        venue: "IndoBlitz Pvt Ltd · Loni, Maharashtra",
        meta: [
            { icon: Calendar, label: "02 April 2026" },
            { icon: MapPin, label: "IndoBlitz HQ · Loni" },
            { icon: Award, label: "Alumna · Pravara Rural Engineering College" },
        ],
        story: [
            "We invited <strong>Ms. Swati Wadekar</strong> - an alumna of <strong>Pravara Rural Engineering College</strong> and now a Software Automation Engineer at <strong>Infineon Technologies, Bengaluru</strong> - to talk to our students and staff about her journey from a rural college in Loni to the global semiconductor industry.",
            "The session covered the realities of campus-to-corporate transition, what large tech employers actually look for, the skills that compound across a career, and the small daily habits that compound into big opportunities. For our team in Loni - most of whom come from the same colleges and the same region Swati did - it was less a lecture and more a glimpse of their own next ten years.",
            "Feedback from students and staff was unanimous: <em>more of this, please</em>. Future dispatches in this series will feature industry leaders, research scientists and rural-origin technologists who have built careers across India and abroad - proof that the path exists, and proof that it starts in places like ours.",
        ],
        photos: [
            { src: "/community/swati-01.jpg", caption: "Hearty welcome poster · IndoBlitz Pvt Ltd, Loni" },
            { src: "/community/swati-03.jpg", caption: "Swati addresses the IndoBlitz team" },
            { src: "/community/swati-04.jpg", caption: "Sharing the campus-to-corporate journey" },
            { src: "/community/swati-02.jpg", caption: "Team photo with the IndoBlitz crew, Loni" },
        ],
        guest: {
            name: "Ms. Swati Wadekar",
            role: "Software Automation Engineer · Infineon Technologies",
            bio: "Alumna of Pravara Rural Engineering College (Savitribai Phule Pune University). Now based in Bengaluru, building automation systems for one of the world's largest semiconductor manufacturers.",
        },
    },
    {
        id: "kolhar-ai-seminar",
        number: "03",
        date: "6–7 March 2026",
        title: "AI gives new direction to rural education.",
        kicker: "National seminar · Keynote",
        venue: "Arts, Science & Commerce College, Kolhar · Ahilyanagar",
        meta: [
            { icon: Calendar, label: "6–7 March 2026" },
            { icon: MapPin, label: "Arts, Science & Commerce College, Kolhar" },
            { icon: Award, label: "NAAC A++ · Savitribai Phule Pune University" },
        ],
        story: [
            "On <strong>Friday, 6th March 2026</strong>, IndoBlitz Founder <strong>Er. Bobby Philips</strong> was invited as the <strong>Keynote Speaker</strong> at the <em>Two-Day National Seminar on Integration of Artificial Intelligence and Machine Learning in Academic Disciplines</em>, held at <strong>Arts, Science and Commerce College, Kolhar</strong> - under the esteemed <strong>Pravara Rural Education Society</strong> and sponsored by <strong>Savitribai Phule Pune University, Pune</strong>.",
            "The seminar was inaugurated by <strong>Hon'ble Sou. Shalinitai Vikhe Patil</strong>, former President of the Ahmednagar Zilla Parishad. The program was chaired by <strong>Dr. Bhaskarrao Kharde Patil</strong>, Chairman of Pravara Sahakari Bank, along with respected academicians, administrators and community leaders.",
            "In his keynote, Bobby spoke about a conviction central to IndoBlitz: <em class='text-gold'>the AI revolution must not remain limited to a few technology hubs. The true strength of India lies in its villages, small towns and the immense talent that exists there.</em> Given the right ecosystem, mentorship and exposure, rural youth can become powerful contributors to the global technology landscape.",
        ],
        quote: "The future of innovation will not be written by cities alone. It will also be shaped in villages where curiosity meets opportunity, and where young minds are given the tools to dream bigger.",
        photos: [
            { src: "/community/kolhar-04.jpg", caption: "Two-Day National Seminar · Inaugural Program · Kolhar" },
            { src: "/community/kolhar-06.jpg", caption: "Dignitaries on the dais · Seminar proceedings released" },
            { src: "/community/kolhar-09.jpg", caption: "Lamp-lighting ceremony · Honouring institutional founders" },
            { src: "/community/kolhar-03.jpg", caption: "Er. Bobby Philips delivering the keynote" },
            { src: "/community/kolhar-08.jpg", caption: "Audience view · Full hall during the inaugural program" },
            { src: "/community/kolhar-07.jpg", caption: "Hon'ble Sou. Shalinitai Vikhe Patil at the podium" },
            { src: "/community/kolhar-05.jpg", caption: "Hon'ble Sou. Shalinitai Vikhe Patil addresses the seminar" },
            { src: "/community/kolhar-02.jpg", caption: "Dignitaries seated in front of the seminar banner" },
            { src: "/community/kolhar-10.jpg", caption: "Engaging with students from the local community" },
            { src: "/community/kolhar-01.jpg", caption: "Dainik Janta Aawaj coverage · 08 March 2026" },
        ],
        dignitaries: [
            { name: "Hon'ble Sou. Shalinitai Vikhe Patil", role: "Chief Guest · Inaugurated the seminar", bio: "Former President, Ahmednagar Zilla Parishad. A long-standing champion of education and rural development across the region." },
            { name: "Dr. Bhaskarrao Kharde Patil", role: "Chairperson", bio: "Chairman, Pravara Sahakari Bank. Quoted in Dainik Janta Aawaj on AI/ML as rapidly developing career fields shaping research, innovation and educational policy." },
            { name: "Er. Bobby Philips", role: "Keynote Speaker · Resource Person", bio: "Founder, IndoBlitz & Pragya Shakti Foundation. Spoke on rural India as the next engine of the global AI workforce and the ecosystem needed to unlock it." },
            { name: "Dr. H.R. Aher", role: "Host · Principal", bio: "Principal, Arts, Science and Commerce College, Kolhar. Signed the official invitation and letter of appreciation on behalf of the institution." },
        ],
        press: {
            outlet: "Dainik Janta Aawaj",
            date: "08 March 2026 · Page 2 · Reporter: Ganesh Somvanshi",
            quote: "\"एआयमुळे शिक्षण क्षेत्राला नवी दिशा\" - \"AI gives new direction to the education sector.\" The article quotes Dr. Bhaskarrao Kharde Patil noting that AI and Machine Learning are career fields set for rapid future growth, and that the seminar would guide new research, innovation and educational policy.",
        },
        letters: [
            { title: "Official Invitation Letter", date: "26 Feb 2026", ref: "Ref: ASCCK/2025-26/14", img: "/community/invitation-letter.jpg", alt: "Official invitation letter dated 26 February 2026" },
            { title: "Letter of Appreciation", date: "06 Mar 2026", ref: "Ref: ASCCK/2025-26/19", img: "/community/appreciation-letter.jpg", alt: "Letter of appreciation dated 6 March 2026" },
        ],
        links: [
            { label: "LinkedIn", href: "https://www.linkedin.com/posts/indoblitz_indoblitz-artificialintelligence-ruralinnovation-activity-7436778333884821505-s39j" },
            { label: "Facebook", href: "https://www.facebook.com/share/14gGFPVSvC2/" },
        ],
    },
    {
        id: "parents-inauguration-2025",
        number: "02",
        date: "08 October 2025",
        title: "Before the work, the blessing.",
        kicker: "A quiet dual inauguration · IndoBlitz + Pragya Shakti Foundation",
        venue: "IndoBlitz Pvt Ltd · Loni, Maharashtra",
        meta: [
            { icon: Calendar, label: "08 October 2025" },
            { icon: MapPin, label: "IndoBlitz HQ · above Ni3 Computer, Loni" },
            { icon: Award, label: "Self-funded · no loans, grants or external funding" },
        ],
        story: [
            "Things take time to come together in a village. Nearly <strong>two full years</strong> after the idea of IndoBlitz was conceived on those August days back at PREC - and every rupee of the infrastructure paid from the founder's own pocket, with no loans, no grants, no crowdfunding - the office above Nitin Pokale's Ni3 Computer was finally ready.",
            "On the morning of <strong>8th October 2025</strong>, two ribbons were cut. There were no dignitaries on the dais. No press. No politician. The first ribbon, for <strong>IndoBlitz Pvt Ltd</strong>, was held by Bobby's <strong>father and mother</strong> - flown in from Kerala for the occasion. The second ribbon, for the <strong>Pragya Shakti Foundation</strong>, was held by his <strong>wife</strong>, his <strong>sister</strong> and his <strong>brother-in-law</strong>.",
            "<em class='text-gold'>\"I exist because of their love, blessings and sacrifices. I just wanted my parents to take home a good memory of the work their son is doing - without much fanfare, without spotlight. What was important to me was their happiness and their blessings.\"</em>",
            "Two ribbons. One family. One blessing. The for-profit engine (IndoBlitz) blessed by the generation that built him; the social impact arm (Pragya Shakti Foundation) blessed by the generation that builds with him. Everything that has followed in the dispatches above - Kolhar's keynote, Swati's industry talk, every cohort yet to come - is, in its quiet centre, an offering back to the people in this room.",
        ],
        photos: [
            { src: "/community/blessing-05.jpg", caption: "IndoBlitz ribbon · Bobby's father and mother together · 08 October 2025" },
            { src: "/community/blessing-07.jpg", caption: "Pragya Shakti Foundation ribbon · sister, wife and brother-in-law cutting together" },
            { src: "/community/blessing-06.jpg", caption: "The blessing moment · mother and family at the ribbon" },
            { src: "/community/blessing-10.jpg", caption: "Lighting the lamp · brother-in-law with parents, wife and sister" },
            { src: "/community/blessing-09.jpg", caption: "Parents inside the new office · inspecting the workstations they had just blessed" },
            { src: "/community/blessing-08.jpg", caption: "The new IndoBlitz workspace · ready for the first cohort" },
            { src: "/community/blessing-01.jpg", caption: "Bobby's parents seated outside the new IndoBlitz office" },
            { src: "/community/blessing-02.jpg", caption: "Arrival in Loni · greeted by family, friends and well-wishers" },
            { src: "/community/blessing-03.jpg", caption: "Family blessings on the morning of the inauguration" },
            { src: "/community/blessing-04.jpg", caption: "Climbing the stairs together · father and son · into the new office" },
        ],
        guest: {
            name: "A note on this dispatch",
            role: "Founder's reflection",
            bio: "There was no fanfare. There was no marketing photographer. These photos were taken by family. They are kept here because the story of what IndoBlitz and Pragya Shakti Foundation have become would be incomplete without the people who blessed both of them before any work began.",
        },
    },
    {
        id: "prec-genesis-2023",
        number: "01",
        date: "August 2023",
        title: "The decision to come home - IndoBlitz begins in Loni.",
        kicker: "Origin story · Pre-IndoBlitz era",
        venue: "Pravara Rural Engineering College (PREC), Loni",
        meta: [
            { icon: Calendar, label: "08 August 2023" },
            { icon: MapPin, label: "PREC Loni · Maharashtra" },
            { icon: Award, label: "Documented by Sachin Nimbalkar, Alumni Cell, PREC Loni" },
        ],
        story: [
            "Long before IndoBlitz existed as a company, there was a three-day visit back to the college that shaped its founder. In August 2023, <strong>Bobby Philips</strong> - an Electronics Engineering alumnus of the <strong>1986 batch</strong> at PREC Loni - returned to campus with a message his old friend Sachin Nimbalkar still remembers: <em>\"Sachin Sir, I am coming to Loni on the 8th. This time I have come thinking something different.\"</em>",
            "What followed over those three days became, in hindsight, the founding act of IndoBlitz. Bobby met the faculty he had grown up under - Principal Gulhane Sir, Additional CEO Hiremath Sir, Dr. Shakeel Sheikh, Rahul Kadu, Kurkute Sir, Rajendra Nimbalkar Sir and many more. He sat across the same canteen tables he had eaten <em>jilebi</em> at as a teenager. He visited Lontech, donated quietly at the charity box, and asked: <em>\"What can we do for the current kids?\"</em>",
            "He delivered <strong>four different sessions</strong> across diploma and degree students - on Artificial Intelligence, Cloud Technology and 3D Printing - urging each student to find where these tools could apply <em>inside their own branch</em>. He committed to helping any student who wanted to study in Australia, offering to personally make introductions to universities and to friends in industry. A one-and-a-half-hour session with the <strong>Nirbhaya Group</strong> students (Siddhartha, Rutuja, Sai, Heramb) ended with him promising to reach his Bollywood network to amplify their mission.",
            "Sachin Sir's tribute closed with an observation that has aged into prophecy: <em class='text-gold'>\"Bobby is in harmony with life and living - a dancer, singer, motivational speaker, a servant of the disabled and the elderly, and most importantly a great human, who does not understand the difference between himself and his friend's religion. He only feels the depth of that friendship.\"</em>",
            "Two and a half years later, IndoBlitz Pvt Ltd is registered, operating out of Loni, building the rural technology ecosystem Bobby walked back to start. Every dispatch above this one - Kolhar's keynote, Swati's industry talk, every cohort yet to come - traces back to the conversations of those three August days.",
        ],
        photos: [
            { src: "/community/prec-genesis-06.jpg", caption: "Department of Electronics & Computer Engineering - coming home, 37 years later" },
            { src: "/community/prec-genesis-08.jpg", caption: "At the PREC podium · delivering one of four sessions to diploma and degree students" },
            { src: "/community/prec-genesis-11.jpg", caption: "Full hall at PREC · Additive Manufacturing workshop · 7–9 Aug 2023" },
            { src: "/community/prec-genesis-12.jpg", caption: "Speaking under the portraits of the institutional founders" },
            { src: "/community/prec-genesis-14.jpg", caption: "Classroom session · Robotics & AI fundamentals with diploma students" },
            { src: "/community/prec-genesis-13.jpg", caption: "Q&A · taking a student question after the session" },
            { src: "/community/prec-genesis-15.jpg", caption: "Nirbhaya Group circle · 90-minute open conversation with Siddhartha, Rutuja, Sai, Heramb and others" },
            { src: "/community/prec-genesis-09.jpg", caption: "Under the portraits of the institutional founders · Pravara Rural Engineering College" },
            { src: "/community/prec-genesis-10.jpg", caption: "Full auditorium · diploma and degree students at the AI / Cloud / 3D Printing session" },
            { src: "/community/prec-genesis-07.jpg", caption: "With Dr. Sanjay L. Kurkute, Head of Department · Electronics & Computer Engineering" },
            { src: "/community/prec-genesis-01.jpg", caption: "PREC Loni alumni reunion · open-air canteen · August 2023" },
            { src: "/community/prec-genesis-02.jpg", caption: "Conversations around the same canteen tables where it all began" },
            { src: "/community/prec-genesis-03.jpg", caption: "A reunion 37 years in the making · embracing an old mentor" },
            { src: "/community/prec-genesis-04.jpg", caption: "With PREC faculty · under the portraits of the institutional founders" },
            { src: "/community/prec-genesis-05.jpg", caption: "Planning sessions with alumni and college administration" },
        ],
        guest: {
            name: "Written by Sachin Nimbalkar Sir",
            role: "Alumni Cell · PREC Loni · 13 August 2023",
            bio: "Sachin Sir's original Marathi tribute captured the warmth, humour and intent of those three days at PREC. The English narrative above is a faithful condensation of his Marathi original, preserving the account while smoothing the auto-translation artefacts. The full Marathi original sits in our archives.",
        },
    },
];

const Dispatch = ({ d }) => (
    <section id={d.id} className="scroll-mt-28 mb-24" data-testid={`dispatch-${d.id}`}>
        <div className="max-w-6xl mx-auto px-5 lg:px-10">
            {/* Header */}
            <div className="flex items-center gap-3 mb-4">
                <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-gold/85 px-2 py-1 border border-gold/40 rounded">Dispatch #{d.number}</span>
                <span className="font-mono text-[10px] uppercase tracking-widest text-ivory/45">{d.kicker}</span>
            </div>
            <h2 className="font-display text-4xl lg:text-5xl text-ivory tracking-tight leading-[1.05]">
                {d.title}
            </h2>
            <div className="mt-5 flex flex-wrap gap-x-7 gap-y-2 text-sm">
                {d.meta.map((m, i) => (
                    <div key={i} className="flex items-center gap-2 text-ivory/70"><m.icon className="w-4 h-4 text-gold" /> {m.label}</div>
                ))}
            </div>

            {/* Story */}
            <div className="mt-8 max-w-3xl text-ivory/75 leading-relaxed space-y-5">
                {d.story.map((p, i) => (
                    <p key={i} dangerouslySetInnerHTML={{ __html: p }} />
                ))}
                {d.quote && (
                    <blockquote className="border-l-2 border-gold pl-5 my-6 italic text-ivory/85 text-lg">
                        "{d.quote}"
                    </blockquote>
                )}
            </div>

            {/* Photos */}
            {d.photos && d.photos.length > 0 && (
                <div className="mt-10">
                    <div className="font-mono text-[11px] uppercase tracking-[0.22em] text-gold/80 mb-4">Field gallery · {d.photos.length} {d.photos.length === 1 ? "frame" : "frames"}</div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                        {d.photos.map((p, i) => (
                            <motion.figure
                                key={p.src}
                                initial={{ opacity: 0, y: 16 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, margin: "-60px" }}
                                transition={{ duration: 0.5, delay: i * 0.04 }}
                                className="glass rounded-xl overflow-hidden"
                            >
                                <img src={p.src} alt={p.caption} loading="lazy" className="w-full aspect-[4/3] object-cover" />
                                <figcaption className="p-3 text-[12px] text-ivory/60 leading-snug">{p.caption}</figcaption>
                            </motion.figure>
                        ))}
                    </div>
                </div>
            )}

            {/* Guest */}
            {d.guest && (
                <div className="mt-10 max-w-3xl glass rounded-xl p-6">
                    <div className="font-mono text-[10px] uppercase tracking-widest text-gold/85 mb-2">Featured guest</div>
                    <h3 className="font-display text-xl text-ivory">{d.guest.name}</h3>
                    <div className="text-[11px] font-mono uppercase tracking-widest text-gold/80 mt-1">{d.guest.role}</div>
                    <p className="text-sm text-ivory/65 mt-3 leading-relaxed">{d.guest.bio}</p>
                </div>
            )}

            {/* Dignitaries */}
            {d.dignitaries && (
                <div className="mt-10">
                    <div className="font-mono text-[11px] uppercase tracking-[0.22em] text-gold/80 mb-4">On the dais</div>
                    <div className="grid sm:grid-cols-2 gap-4">
                        {d.dignitaries.map((dg) => (
                            <div key={dg.name} className="glass rounded-xl p-5">
                                <h3 className="font-display text-lg text-ivory">{dg.name}</h3>
                                <div className="text-[11px] font-mono uppercase tracking-widest text-gold/80 mt-1">{dg.role}</div>
                                <p className="text-sm text-ivory/65 mt-3 leading-relaxed">{dg.bio}</p>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Press */}
            {d.press && (
                <div className="mt-10 max-w-3xl">
                    <div className="font-mono text-[11px] uppercase tracking-[0.22em] text-gold/80 mb-4">Press coverage</div>
                    <div className="glass rounded-xl p-6">
                        <div className="font-display text-xl text-ivory">{d.press.outlet}</div>
                        <div className="text-[12px] font-mono uppercase tracking-widest text-ivory/45 mt-1">{d.press.date}</div>
                        <p className="mt-3 text-ivory/70 text-sm leading-relaxed italic">{d.press.quote}</p>
                    </div>
                </div>
            )}

            {/* Letters */}
            {d.letters && (
                <div className="mt-10">
                    <div className="font-mono text-[11px] uppercase tracking-[0.22em] text-gold/80 mb-4">Institutional endorsements</div>
                    <div className="grid sm:grid-cols-2 gap-5">
                        {d.letters.map((l) => (
                            <a key={l.title} href={l.img} target="_blank" rel="noopener noreferrer" className="glass rounded-xl overflow-hidden group flex flex-col hover:border-gold/40 transition-all">
                                <div className="aspect-[8.5/11] bg-ivory/95 overflow-hidden">
                                    <img src={l.img} alt={l.alt} loading="lazy" className="w-full h-full object-contain transition-transform duration-500 group-hover:scale-[1.02]" />
                                </div>
                                <div className="p-4 bg-graphite/40">
                                    <div className="font-mono text-[10px] uppercase tracking-widest text-gold/85">{l.date}</div>
                                    <div className="mt-1 font-display text-lg text-ivory leading-tight">{l.title}</div>
                                    <div className="mt-1 text-[11px] text-ivory/55">{l.ref}</div>
                                </div>
                            </a>
                        ))}
                    </div>
                </div>
            )}

            {/* External links */}
            {d.links && (
                <div className="mt-10 max-w-3xl">
                    <div className="font-mono text-[11px] uppercase tracking-[0.22em] text-gold/80 mb-4">Original posts</div>
                    <div className="flex flex-col sm:flex-row gap-3">
                        {d.links.map((l) => (
                            <a key={l.label} href={l.href} target="_blank" rel="noopener noreferrer" className="flex-1 glass rounded-xl p-4 hover:border-gold/40 transition group">
                                <div className="text-[10px] font-mono uppercase tracking-widest text-gold/80">{l.label}</div>
                                <div className="mt-1 text-ivory text-sm flex items-center gap-2">View original post <ExternalLink className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform" /></div>
                            </a>
                        ))}
                    </div>
                </div>
            )}
        </div>
    </section>
);

const CommunityImpact = () => {
    useEffect(() => {
        document.title = "Community Impact · Field Dispatches · IndoBlitz";
        if (!window.location.hash) window.scrollTo(0, 0);
    }, []);

    return (
        <>
            <Nav />
            <main className="relative pt-28 pb-16" data-testid="community-impact-page">
                <div className="max-w-6xl mx-auto px-5 lg:px-10">
                    <Link to="/" className="inline-flex items-center gap-2 text-ivory/60 hover:text-gold transition-colors text-sm font-mono uppercase tracking-widest" data-testid="back-home">
                        <ArrowLeft className="w-4 h-4" /> Back to indoblitz.com
                    </Link>
                </div>

                {/* Page hero */}
                <section className="max-w-6xl mx-auto px-5 lg:px-10 mt-10 mb-12">
                    <div className="pill"><span className="accent-dot" />Community Impact · Field dispatches</div>
                    <h1 className="mt-5 font-display text-5xl lg:text-7xl text-ivory tracking-tight leading-[1.02]">
                        On the ground in <span className="italic text-gold-shimmer">rural India.</span>
                    </h1>
                    <p className="mt-6 text-ivory/70 text-lg max-w-3xl leading-relaxed">
                        A running archive of seminars, school visits, industry talks and field engagements through which IndoBlitz and the Pragya Shakti Foundation bring AI literacy, mentorship and global career exposure to rural India - one classroom and one cohort at a time.
                    </p>
                </section>

                {/* Dispatches index */}
                <section className="max-w-6xl mx-auto px-5 lg:px-10 mb-16">
                    <div className="font-mono text-[11px] uppercase tracking-[0.22em] text-gold/80 mb-4">Dispatches index</div>
                    <div className="grid sm:grid-cols-2 gap-4">
                        {DISPATCHES.map((d) => (
                            <a key={d.id} href={`#${d.id}`} className="glass rounded-xl p-5 group hover:border-gold/40 transition-all flex items-start gap-4">
                                <div className="font-display text-3xl text-gold/80 leading-none shrink-0">#{d.number}</div>
                                <div className="flex-1 min-w-0">
                                    <div className="font-mono text-[10px] uppercase tracking-widest text-gold/75">{d.kicker}</div>
                                    <div className="mt-1 font-display text-lg text-ivory leading-snug">{d.title}</div>
                                    <div className="mt-2 text-[11px] font-mono uppercase tracking-widest text-ivory/45">{d.date}</div>
                                </div>
                                <ArrowUpRight className="w-4 h-4 text-ivory/35 group-hover:text-gold group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-all shrink-0 mt-1" />
                            </a>
                        ))}
                    </div>
                </section>

                {/* Dispatches */}
                {DISPATCHES.map((d) => <Dispatch key={d.id} d={d} />)}

                {/* Bottom CTA */}
                <section className="max-w-3xl mx-auto px-5 lg:px-10 text-center">
                    <h2 className="font-display text-3xl lg:text-4xl text-ivory tracking-tight">
                        Want to host us - or speak at <span className="italic text-gold-shimmer">our next dispatch?</span>
                    </h2>
                    <p className="mt-3 text-ivory/65">
                        We're actively seeking industry leaders, research scientists and rural-origin technologists to address our cohorts in Loni. If your institution would like a keynote or workshop, or if you'd like to come and speak - we'd love to hear from you.
                    </p>
                    <Link to="/#contact" className="btn-gold mt-6 inline-flex" data-testid="invite-cta">
                        Get in touch
                    </Link>
                </section>
            </main>
            <Footer />
        </>
    );
};

export default CommunityImpact;
